/**
 * Impresión del ticket.
 *
 * El navegador **no habla con impresoras térmicas** de forma confiable. La
 * solución de verdad es un agente local —un pequeño servicio que escucha en
 * `localhost` y traduce a ESC/POS— y este archivo es el cliente de ese agente.
 *
 * Mientras el agente no exista (llega con la Fase 9), hay dos caminos de
 * respaldo que funcionan hoy:
 *
 * 1. Si `VITE_PRINT_AGENT_URL` está definida y responde, se manda el ticket.
 * 2. Si no, se imprime por el navegador en un iframe oculto con el ancho de la
 *    impresora (58 u 80 mm).
 *
 * Y siempre queda el PDF que el navegador ofrece: un POS sin salida de
 * emergencia para imprimir es un POS que se detiene.
 */

import { printerManager } from "./escpos/manager";
import type { SalePublic } from "../lib/offline/db";

export interface TicketContext {
  businessName: string;
  branchName: string;
  cashierName: string;
  /** Ancho del papel en milímetros: 58 u 80. */
  paperWidth: 58 | 80;
  currency?: string;
  slogan?: string;
  rfc?: string | null;
  taxRegime?: string | null;
  address?: string | null;
  phone?: string | null;
  showCashier?: boolean;
  showBranch?: boolean;
  showFolio?: boolean;
  showTaxBreakdown?: boolean;
  headerNote?: string;
  footerNote?: string;
  billingInstructions?: string;
}

const AGENT_URL: string = import.meta.env.VITE_PRINT_AGENT_URL ?? "";

function money(cents: number, currency = "MXN"): string {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function line(left: string, right: string, width: number): string {
  const space = Math.max(1, width - left.length - right.length);
  return left + " ".repeat(space) + right;
}

/**
 * Ticket en texto plano, que es lo que entiende una impresora térmica.
 *
 * Se genera en texto y no en HTML porque el agente lo manda tal cual a la
 * impresora: convertir HTML a ESC/POS es donde se pierden los acentos y las
 * columnas de precios.
 */
export function buildTicketText(sale: SalePublic, context: TicketContext): string {
  const width = context.paperWidth === 58 ? 32 : 48;
  const rule = "-".repeat(width);
  const lines: string[] = [];

  lines.push(context.businessName.toUpperCase().slice(0, width));
  if (context.slogan) {
    lines.push(context.slogan.slice(0, width));
  }
  if (context.rfc) {
    lines.push(`RFC: ${context.rfc}`.slice(0, width));
  }
  if (context.taxRegime) {
    lines.push(`Regimen Fiscal: ${context.taxRegime}`.slice(0, width));
  }
  if (context.address) {
    lines.push(context.address.slice(0, width));
  }
  if (context.phone) {
    lines.push(`Tel: ${context.phone}`.slice(0, width));
  }
  if (context.showBranch !== false && context.branchName) {
    lines.push(`Sucursal: ${context.branchName}`.slice(0, width));
  }
  if (context.headerNote) {
    lines.push(rule);
    lines.push(context.headerNote.slice(0, width));
  }
  lines.push(rule);
  if (context.showFolio !== false) {
    lines.push(line(`Ticket ${sale.series}-${sale.folio}`, sale.business_date, width));
  }
  if (context.showCashier !== false) {
    lines.push(line("Cajero", context.cashierName, width));
  }
  lines.push(rule);

  for (const item of sale.lines ?? []) {
    lines.push(item.name.slice(0, width));
    const detail = `${item.quantity} x ${money(item.unit_price_cents, context.currency)}`;
    lines.push(line(`  ${detail}`, money(item.total_cents, context.currency), width));
    if (item.discount_cents > 0) {
      lines.push(line("  Descuento", `-${money(item.discount_cents, context.currency)}`, width));
    }
  }

  lines.push(rule);
  lines.push(line("Subtotal", money(sale.subtotal_cents, context.currency), width));
  if (sale.discount_cents > 0) {
    lines.push(line("Descuento", `-${money(sale.discount_cents, context.currency)}`, width));
  }
  if (context.showTaxBreakdown !== false) {
    lines.push(line("IVA", money(sale.tax_cents, context.currency), width));
  }
  lines.push(line("TOTAL", money(sale.total_cents, context.currency), width));
  lines.push(rule);

  for (const payment of sale.payments ?? []) {
    lines.push(line(payment.method, money(payment.amount_cents, context.currency), width));
  }
  if (sale.change_cents > 0) {
    lines.push(line("Cambio", money(sale.change_cents, context.currency), width));
  }
  if (sale.tip_cents > 0) {
    lines.push(line("Propina", money(sale.tip_cents, context.currency), width));
  }

  lines.push(rule);
  if (context.footerNote) {
    lines.push(context.footerNote);
  } else {
    lines.push("¡Gracias por su compra!");
  }
  if (context.billingInstructions) {
    lines.push("");
    lines.push(context.billingInstructions);
  }
  lines.push("");
  lines.push("");
  return lines.join("\n");
}

/** ¿Hay agente local configurado y responde? */
export async function agentAvailable(): Promise<boolean> {
  if (!AGENT_URL) return false;
  try {
    const response = await fetch(`${AGENT_URL}/health`, { signal: AbortSignal.timeout(400) });
    return response.ok;
  } catch {
    return false;
  }
}

/** Manda el ticket al agente local. Devuelve `false` si no se pudo. */
export async function printViaAgent(
  sale: SalePublic,
  context: TicketContext,
): Promise<boolean> {
  if (!AGENT_URL) return false;
  try {
    const response = await fetch(`${AGENT_URL}/print`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text: buildTicketText(sale, context),
        paper_width: context.paperWidth,
        open_drawer: (sale.payments ?? []).some((payment) => payment.method === "efectivo"),
      }),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Impresión de respaldo por el navegador.
 *
 * Se usa un iframe oculto en vez de `window.print()` sobre la página: así sólo
 * sale el ticket y no toda la interfaz del POS.
 */
export function printViaBrowser(sale: SalePublic, context: TicketContext): void {
  const width = context.paperWidth === 58 ? "58mm" : "80mm";
  const text = buildTicketText(sale, context);

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    document.body.removeChild(iframe);
    return;
  }
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>Ticket</title>
    <style>
      @page { size: ${width} auto; margin: 2mm; }
      body { font-family: "JetBrains Mono", ui-monospace, monospace; font-size: 11px;
             white-space: pre-wrap; margin: 0; }
    </style></head><body>${escapeHtml(text)}</body></html>`);
  doc.close();

  iframe.contentWindow?.focus();
  iframe.contentWindow?.print();
  // Se retira cuando el navegador ya lanzó el diálogo.
  setTimeout(() => document.body.removeChild(iframe), 1_000);
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Imprime por donde se pueda: USB directo, Bluetooth, agente local o diálogo de navegador. */
export async function printTicket(
  sale: SalePublic,
  context: TicketContext,
): Promise<"usb" | "bluetooth" | "agente" | "navegador"> {
  const result = await printerManager.printSale(sale, context);
  if (result.destination === "usb") return "usb";
  if (result.destination === "bluetooth") return "bluetooth";
  if (result.destination === "agent") return "agente";

  printViaBrowser(sale, context);
  return "navegador";
}
